import { Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';
import { FileResizeModalComponent } from 'app/shared/file-resize-modal/file-resize-modal.component';
import { ModalsService } from 'app/shared/ui/modals/modals.service';
import { FileUploadRequest } from './file-drop.model';
import { UiModule } from '../ui';
import { FileDropDirective } from './file-drop.directive';

@Component({
    selector: 'image-upload',
    imports: [
        UiModule,
        FileDropDirective,
    ],
    styles: [
        `
        :host {
            display: block;
        }
        .image .img {
            max-width: 100%;
            max-height: 280px;
        }
        .drop-zone {
            border: dashed 2px transparent;
            width: 100%;
            min-height: 100px;
        }
        .drop-message {
            color: #516173;
            font-size: 11px;
        }
        .drop-message p {
            margin-bottom: 3px;
        }

        .dz-active {
            border: dashed 2px #0f72bc;
        }
        .dz-active .drop-message {
            color: #0f72bc;
        }

        .hidden {
            top: -9999px;
            position: fixed;
        }

        .no-image {
            display: flex;
            height: 175px;
            align-items: center;
            background: #eaeaea;
            color: #193649;
            font-weight: bolder;
            cursor: pointer;
            border: dashed 2px #193649;
        }
        .btn-toolbar {
            column-gap: 0.4rem;
        }
        .row {
            min-height: 130px;
        }
        `,
    ],
    template: `
        @if (!inLoading) {
            <div
                fileDrop
                [class.dz-active]="fileOver"
                (onFileDrop)="onFileDrop($event)"
                (fileOver)="onFileOver($event)"
                class="drop-zone">
                <div class="row">
                    <div class="col-md-5">
                        <div class="image">
                            @if (fileUrl) {
                                <img alt="" class="img" [src]="fileUrl">
                            }
                            @else {
                                <div role="button" class="no-image" (click)="triggerFileUpload()">
                                    <div class="text-center w-100">No image uploaded</div>
                                </div>
                            }
                        </div>
                    </div>
                    <div class="col-md-7">
                        <div class="drop-message">
                            <p>{{ description }}</p>
                        </div>
                        <input
                            type="file"
                            class="hidden"
                            accept="image/png, image/jpg, image/jpeg, image/gif"
                            #fileUpload
                            (change)="onFileUploadChange($event)">
                        <div class="btn-toolbar mt-2 mb-1">
                            <button type="button" class="btn btn-sm btn-secondary" (click)="triggerFileUpload()">
                                <i class="fa fa-upload" aria-hidden="true"></i>
                                Choose
                            </button>
                            <button type="button" class="btn btn-sm btn-danger" [disabled]="!fileUrl" (click)="onRemoveFile()">
                                <i class="fa fa-times" aria-hidden="true"></i>
                                Remove
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        }
        @else {
            <ui-block-loading></ui-block-loading>
        }
    `,
})
export class ImageUploadComponent {

    @ViewChild('fileUpload') fileUploadHandler: ElementRef;

    private ALLOWED_EXTENSIONS = ['png', 'jpg', 'jpeg', 'gif'];

    @Input() folder: string;

    @Input() fileUrl: string | null;

    @Input() description = 'Drop image here to upload or choose from disk.';

    @Output() fileUploaded = new EventEmitter<FileUploadRequest>();

    @Output() fileRemoved = new EventEmitter<string>();

    @Input() inLoading: boolean;

    @Input() withResize = false;

    @Input() aspectRatio = 1;

    // if set - resize final image down to given width; 0 = disabled
    @Input() maxWidth = 0;

    fileOver = false;

    constructor(private modalsService: ModalsService) {}

    triggerFileUpload() {
        this.fileUploadHandler.nativeElement.click();
    }

    onFileUploadChange(event: any) {
        if (!event.target || event.target.files.length === 0) {
            return;
        }
        const file = event.target.files.item(0);
        return this.processFile(event, file);
    }

    private processFile(event: any, file: File) {
        const extension = file.name
            .toLowerCase()
            .split('.')
            .reverse()[0];
        if (!this.ALLOWED_EXTENSIONS.includes(extension)) {
            this.modalsService.alert('Please upload a valid image. Accepted types are PNG, JPG and GIF', 'No image detected', 'warning');
            return;
        }

        if (this.withResize) {
            this.openImageResizeModal(event);
        } else {
            this.emitUploadedFile(file);
        }
    }

    private emitUploadedFile(file: File) {
        const fd = new FormData();
        fd.append('file', file, file.name);
        this.fileUploaded.emit({
            formData: fd,
            folder: this.folder,
        });
    }

    private openImageResizeModal(uploadEvent: Event) {
        this.modalsService
            .openModal(FileResizeModalComponent, {
                uploadEvent,
                maxWidth: this.maxWidth,
                aspectRatio: this.aspectRatio,
            })
            .result.subscribe({
                next: file => {
                    this.emitUploadedFile(file);
                },
                error: error => {
                    if (error.message) {
                        this.modalsService.alert(error.message, 'Error', 'danger');
                    }
                },
                complete: () => {
                    // when done, fileInput needs to be reset, otherwise (change) won't be triggered with same file
                    this.fileUploadHandler.nativeElement.value = '';
                }
            });
    }

    onFileOver(val: boolean) {
        this.fileOver = val;
    }

    onFileDrop(fileList: FileList) {
        const file = fileList.item(0);
        const event = {
            target: { files: [file] },
        };
        if (file) {
            return this.processFile(event, file);
        }
    }

    onRemoveFile() {
        this.modalsService.confirm('Are you sure, you want to delete the image?', 'Question', 'danger').result.subscribe(_ => {
            this.fileRemoved.emit(this.folder);
        });
    }
}
